import { Pipe, PipeTransform } from '@angular/core';

/**
 * Local replacement for the utcToLocal pipe in @digi-america/shared, used by the
 * deal desk pages only.
 *
 * The shared pipe subtracts getTimezoneOffset() before formatting, which is a
 * compensation for endpoints that serialize timestamps with no timezone
 * designator - new Date() reads those as local time, and the subtraction cancels
 * that out. Deal desk stores its timestamps as UTC (see the DateTimeKind.Utc on
 * DealDesk.CreatedAt), so they arrive with a trailing Z, already parse correctly,
 * and the compensation shifts them by a second offset - 19:41 UTC rendering as
 * 11:41 EDT instead of 15:41.
 *
 * This version normalizes the input instead of adjusting the output: a timestamp
 * carrying no timezone is treated as UTC, and formatting is left to
 * toLocaleString. That is correct under either convention, so the remaining
 * endpoints can move to timestamptz without this needing another look.
 *
 * Once the fix ships in @digi-america/shared, delete this and point the deal desk
 * components back at the shared pipe - the pipe name is unchanged, so only the
 * imports move.
 */
@Pipe({
    name: 'utcToLocal',
    standalone: true
})
export class UtcToLocalTimePipe implements PipeTransform {
    private static readonly HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

    transform(utcDateString: string | null | undefined, format = 'medium'): string {
        if (!utcDateString) {
            return '';
        }

        const trimmed = utcDateString.trim();
        const date = new Date(
            UtcToLocalTimePipe.HAS_TIMEZONE.test(trimmed) ? trimmed : `${trimmed}Z`
        );

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        };

        if (format === 'short') {
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        if (format === 'full') {
            return date.toLocaleString(undefined, {
                ...options,
                weekday: 'long',
                month: 'long'
            });
        }

        // 'medium', and anything unrecognized, fall through to the default.
        return date.toLocaleString(undefined, options);
    }
}
